const resources = {
  "en": {
    "forecastSummaryCalendar": {
      "title": "Forecast Summary Calendar",
      "filters": {
        "anchorDay": "Anchor Day",
        "monthsAhead": "Months Ahead",
        "boostModeOn": "Boost Mode: On",
        "boostModeOff": "Boost Mode: Off"
      },
      "overview": {
        "anchorDate": "Anchor Date",
        "startingBalance": "Starting Balance",
        "startingBalanceSource": {
          "live": "Source: live spendable balances from selected accounts.",
          "ledger": "Source: rebuilt from the native transaction ledger because live balances were unavailable.",
          "empty": "Source: no tracked accounts are selected."
        },
        "sourceBreakdown": {
          "title": "Starting balance sources",
          "description": "Selected accounts included in the starting balance. Credit cards use available credit; cash accounts use current spendable balance.",
          "accountFallback": "Account • {{id}}",
          "accountTypeFallback": "account",
          "amountUnavailable": "Unavailable",
          "status": {
            "included": "Included",
            "skipped": "Skipped"
          },
          "sources": {
            "computedAccountBalance": "Current account balance",
            "computedAvailableCredit": "Available credit from current debt",
            "explicitAvailableCredit": "Available credit from account data",
            "explicitAccountBalance": "Live account balance",
            "legacyCreditLimitUsedLimit": "Legacy credit limit minus used limit",
            "unknown": "Unknown source"
          }
        },
        "totalIncome": "Total Expected Income",
        "totalExpense": "Total Expected Expense",
        "netForecast": "Net Forecast",
        "closingBalance": "Closing Balance",
        "boostsLabel": "✨ Personal Boosts",
        "boostsCaption": "Active adjustments scheduled",
        "riskAlert": "⚠️ Balance may go negative"
      },
      "executionMode": {
        "ledgerFallbackShadow": "Admin-only fallback forecast",
        "ledgerFallbackLimitedRollout": "Fallback forecast active",
        "ledgerFallbackUserSummary": "Estimated ledger forecast"
      },
      "executionModeDescriptions": {
        "ledgerFallbackShadow": "This forecast is using the guarded native-ledger fallback path for operator proof only.",
        "ledgerFallbackLimitedRollout": "This forecast is temporarily using the guarded native-ledger fallback path because the live-balance mix is still under bounded rollout.",
        "ledgerFallbackUserSummary": "Live balances are unavailable for the selected accounts, so the summary is rebuilt from confirmed transactions and valued into the forecast currency."
      },
      "blocked": {
        "title": "Forecast blocked for this account mix",
        "description": "The selected live-balance accounts span multiple currencies, so this forecast stays in protected mode.",
        "summary": "Keep the protected guard in place and review the live-balance source setup before relying on forecast balances.",
        "stats": {
          "selectedAccounts": "{{count}} selected accounts",
          "resolvedAccounts": "{{count}} resolved accounts",
          "liveBalanceReady": "{{count}} with live balances",
          "missingLiveBalances": "{{count}} missing live balances",
          "currencies": "Currencies: {{currencies}}"
        },
        "actions": {
          "label": "Next step",
          "none": "No immediate remediation is required. The protected guard stays in place because the selection is mixed-currency.",
          "review_missing_selected_accounts": "Review the selected accounts list. Some configured live-balance accounts could not be resolved.",
          "review_live_balance_source_coverage": "Review live-balance coverage for the selected accounts before relying on this forecast.",
          "consider_native_ledger_fallback": "A native-ledger fallback preview looks viable, but it remains preview-only for now.",
          "unknown": "Review the selected live-balance account configuration."
        },
        "previewAvailable": "Native-ledger fallback preview is available for operator inspection.",
        "fallbackAction": {
          "open": "Open fallback forecast"
        },
        "preview": {
          "title": "Admin shadow preview",
          "startingBalance": "Starting balance: {{amount}}",
          "accountsValued": "{{valued}} / {{total}} accounts valued",
          "sources": {
            "liveBalance": "Live-balance shadow",
            "ledgerFallback": "Native-ledger fallback"
          },
          "completeness": {
            "complete": "Complete coverage",
            "partial": "Partial coverage"
          },
          "freshness": {
            "fresh": "Fresh quotes",
            "stale": "Stale quotes",
            "degraded": "Degraded freshness"
          }
        },
        "execution": {
          "title": "Fallback forecast preview",
          "description": "Projected closing balance: {{closingBalance}}",
          "entriesValued": "{{valued}} / {{total}} forecast entries valued",
          "risk": {
            "stable": "Preview stays above zero",
            "negative": "Preview may go negative"
          },
          "metrics": {
            "startingBalance": "Preview starting balance: {{amount}}",
            "expectedIncome": "Preview expected income: {{amount}}",
            "expectedExpense": "Preview expected expense: {{amount}}"
          }
        }
      },
      "calendar": {
        "balanceLabel": "Balance:",
        "summaryLabels": {
          "income": "Income planned for this day",
          "expense": "Expenses planned for this day",
          "mixed": "Income and expenses planned for this day",
          "empty": "No activity planned for this day"
        },
        "summaryAria": "{{label}} on {{date}}",
        "chipLabels": {
          "income": "Income",
          "expense": "Expenses"
        },
        "entryCount": {
          "income": "{{count}} income",
          "expense": "{{count}} payments"
        },
        "chipAria": {
          "income": "Income planned for {{date}}",
          "expense": "Expenses planned for {{date}}"
        },
        "accountFallback": "Account • {{id}}",
        "paysFrom": "Pays from: {{account}}",
        "receivesTo": "To: {{account}}",
        "projectedAfterExpense": "Account after payment: {{amount}}",
        "projectedAfterIncome": "Account after income: {{amount}}",
        "accountInsufficient": "Not enough on this account/card",
        "riskBadge": "Needs funds",
        "actionBadge": "Needs action",
        "missedCommitmentBadge": "Promise missed",
        "pendingAction": "Pending task: confirm or cancel it",
        "overdueAction": "Overdue task: confirm or cancel it",
        "overdueActionWithDate": "Overdue task, originally due {{date}}. Confirm or cancel it.",
        "missedCommitmentAction": "Missed promise: confirm or cancel it today.",
        "missedCommitmentActionWithDays": "Missed promise: {{count}} days late. Confirm or cancel it today.",
        "boostButton": {
          "manage": "Manage Personal Boosts",
          "start": "Start a Personal Boost"
        },
        "boostTooltip": {
          "manage": "Manage ✨ Personal Boosts",
          "count_one": "You have {{count}} boost scheduled for this day.",
          "count_other": "You have {{count}} boosts scheduled for this day.",
          "start": "Start a ✨ Personal Boost to adjust this day."
        }
      },
      "adjustments": {
        "boostPrefix": "✨ Personal Boost:",
        "repaymentPlanPrefix": "Repayment plan:",
        "defaultIncome": "Income boost",
        "defaultExpense": "Expense boost",
        "boostEntry": "✨ Personal Boost entry",
        "noDescription": "No description",
        "logTitle": "✨ Personal Boost Log",
        "incomeTag": "Income Boost",
        "expenseTag": "Expense Boost",
        "removeConfirm": "Remove this ✨ Personal Boost?",
        "removeAction": "Remove",
        "modalTitle": "✨ Personal Boost Forecast",
        "modalTitleForDate": "✨ Personal Boost for {{date}}",
        "modalOk": "Save Personal Boost",
        "modalCancel": "Close",
        "fields": {
          "date": "Date",
          "type": "Boost Type",
          "account": "Account",
          "accountExpenseExtra": "Choose the account or card this expense should affect.",
          "accountIncomeExtra": "Choose the account that receives this income.",
          "accountPlaceholder": "Select account",
          "amount": "Amount",
          "description": "Description",
          "descriptionExtra": "Optional note to identify this boost",
          "descriptionPlaceholder": "e.g. Paycheck sparkle"
        },
        "validation": {
          "type": "Choose a boost type",
          "account": "Choose an account for this boost",
          "amount": "Enter boost amount"
        }
      },
      "liveAccounts": {
        "title": "Live Source Accounts",
        "empty": "None"
      },
      "monthlySummary": {
        "title": "Monthly Summary",
        "help": "Monthly totals of forecasted incomes and expenses that affect tracked accounts. Transfers are excluded. Net = income − expense.",
        "rowCompact": "{{month}} — {{income}} • {{expense}} • {{net}}",
        "totalNet": "Total Net",
        "row": "{{month}}: Income {{income}} | Expense {{expense}} | Net {{net}}",
        "empty": "No data"
      },
      "legend": {
        "tonePositive": "Positive day",
        "toneNegative": "Negative day"
      },
      "dayDrawer": {
        "title": "Day details",
        "income": "Income",
        "expenses": "Expenses",
        "net": "Net",
        "noIncome": "No income scheduled.",
        "noExpenses": "No expenses scheduled.",
        "manageBoosts": "Manage ✨ Personal Boosts",
        "startBoost": "Start ✨ Personal Boost"
      },
      "notifications": {
        "loadFailed": "Failed to load forecast summary",
        "historyLoadFailed": "Failed to load questions",
        "deleteSuccess": "Deleted",
        "deleteFailed": "Failed to delete question",
        "dateBeforeRange": "No data before start date",
        "dateAfterRange": "No data beyond forecast range",
        "dateOutOfRange": "Selected date is outside the forecast range",
        "boostSaved": "✨ Personal Boost saved",
        "boostSavedStale": "✨ Personal Boost saved, but forecast refresh failed. Data will sync on next load.",
        "boostSaveFailed": "Failed to save ✨ Personal Boost",
        "boostRemoved": "✨ Personal Boost removed",
        "boostRemovedStale": "✨ Personal Boost removed, but forecast refresh failed. Data will sync on next load.",
        "boostRemoveFailed": "Failed to remove ✨ Personal Boost"
      }
    }
  },
  "uk": {
    "forecastSummaryCalendar": {
      "title": "Календар підсумків прогнозу",
      "filters": {
        "anchorDay": "Опорний день",
        "monthsAhead": "Місяців уперед",
        "boostModeOn": "Режим бусту: увімкнено",
        "boostModeOff": "Режим бусту: вимкнено"
      },
      "overview": {
        "anchorDate": "Опорна дата",
        "startingBalance": "Початковий баланс",
        "startingBalanceSource": {
          "live": "Джерело: доступні live-баланси вибраних рахунків.",
          "ledger": "Джерело: відновлено з нативного журналу транзакцій, бо live-баланси були недоступні.",
          "empty": "Джерело: не вибрано жодного відстежуваного рахунку."
        },
        "sourceBreakdown": {
          "title": "Джерела початкового балансу",
          "description": "Вибрані рахунки, які увійшли в початковий баланс. Для кредиток використовується доступний кредит; для грошових рахунків — поточний доступний баланс.",
          "accountFallback": "Рахунок • {{id}}",
          "accountTypeFallback": "рахунок",
          "amountUnavailable": "Недоступно",
          "status": {
            "included": "Враховано",
            "skipped": "Пропущено"
          },
          "sources": {
            "computedAccountBalance": "Поточний баланс рахунку",
            "computedAvailableCredit": "Доступний кредит з поточного боргу",
            "explicitAvailableCredit": "Доступний кредит з даних рахунку",
            "explicitAccountBalance": "Live-баланс рахунку",
            "legacyCreditLimitUsedLimit": "Ліміт кредиту мінус використаний ліміт",
            "unknown": "Невідоме джерело"
          }
        },
        "totalIncome": "Загальний очікуваний дохід",
        "totalExpense": "Загальні очікувані витрати",
        "netForecast": "Чистий прогноз",
        "closingBalance": "Підсумковий баланс",
        "boostsLabel": "✨ Персональні бусти",
        "boostsCaption": "Запланованих коригувань",
        "riskAlert": "⚠️ Баланс може стати відʼємним"
      },
      "executionMode": {
        "ledgerFallbackShadow": "Fallback forecast лише для admin",
        "ledgerFallbackLimitedRollout": "Fallback forecast увімкнено",
        "ledgerFallbackUserSummary": "Оціночний ledger forecast"
      },
      "executionModeDescriptions": {
        "ledgerFallbackShadow": "Цей прогноз використовує захищений native-ledger fallback лише для operator proof.",
        "ledgerFallbackLimitedRollout": "Цей прогноз тимчасово використовує захищений native-ledger fallback, бо змішаний live-balance набір ще залишається в межах bounded rollout.",
        "ledgerFallbackUserSummary": "Live-баланси для вибраних рахунків недоступні, тому сводка відновлена з підтверджених транзакцій і перерахована у валюту прогнозу."
      },
      "blocked": {
        "title": "Прогноз заблоковано для цього набору рахунків",
        "description": "Вибрані рахунки для live-балансу мають різні валюти, тому прогноз залишається в захищеному режимі.",
        "summary": "Залишайте захисний guard увімкненим і перевірте конфігурацію джерел live-балансу, перш ніж покладатися на цей прогноз.",
        "stats": {
          "selectedAccounts": "{{count}} вибраних рахунків",
          "resolvedAccounts": "{{count}} вирішених рахунків",
          "liveBalanceReady": "{{count}} з live-балансами",
          "missingLiveBalances": "{{count}} без live-балансів",
          "currencies": "Валюти: {{currencies}}"
        },
        "actions": {
          "label": "Наступний крок",
          "none": "Негайна ремедіація не потрібна. Захисний guard залишається ввімкненим, бо вибірка мультивалютна.",
          "review_missing_selected_accounts": "Перевірте список вибраних рахунків. Частину налаштованих джерел live-балансу не вдалося знайти.",
          "review_live_balance_source_coverage": "Перевірте покриття live-балансів для вибраних рахунків, перш ніж покладатися на цей прогноз.",
          "consider_native_ledger_fallback": "Preview нативного ledger fallback виглядає життєздатним, але поки що лишається лише preview.",
          "unknown": "Перевірте конфігурацію вибраних джерел live-балансу."
        },
        "previewAvailable": "Preview нативного ledger fallback доступний для operator inspection.",
        "fallbackAction": {
          "open": "Відкрити fallback forecast"
        },
        "preview": {
          "title": "Admin shadow preview",
          "startingBalance": "Початковий баланс: {{amount}}",
          "accountsValued": "Оцінено рахунків: {{valued}} / {{total}}",
          "sources": {
            "liveBalance": "Live-balance shadow",
            "ledgerFallback": "Native-ledger fallback"
          },
          "completeness": {
            "complete": "Повне покриття",
            "partial": "Часткове покриття"
          },
          "freshness": {
            "fresh": "Свіжі котирування",
            "stale": "Застарілі котирування",
            "degraded": "Погіршена свіжість"
          }
        },
        "execution": {
          "title": "Preview fallback forecast",
          "description": "Прогнозований closing balance: {{closingBalance}}",
          "entriesValued": "Оцінено forecast entries: {{valued}} / {{total}}",
          "risk": {
            "stable": "Preview лишається вище нуля",
            "negative": "Preview може піти в мінус"
          },
          "metrics": {
            "startingBalance": "Preview starting balance: {{amount}}",
            "expectedIncome": "Preview expected income: {{amount}}",
            "expectedExpense": "Preview expected expense: {{amount}}"
          }
        }
      },
      "calendar": {
        "balanceLabel": "Баланс:",
        "summaryLabels": {
          "income": "Дохід заплановано на цей день",
          "expense": "Витрати заплановано на цей день",
          "mixed": "Дохід і витрати заплановано на цей день",
          "empty": "Немає запланованої активності"
        },
        "summaryAria": "{{label}} на {{date}}",
        "chipLabels": {
          "income": "Дохід",
          "expense": "Витрати"
        },
        "entryCount": {
          "income": "{{count}} доходи",
          "expense": "{{count}} платежі"
        },
        "chipAria": {
          "income": "Дохід заплановано на {{date}}",
          "expense": "Витрати заплановано на {{date}}"
        },
        "accountFallback": "Рахунок • {{id}}",
        "paysFrom": "Оплата з: {{account}}",
        "receivesTo": "На рахунок: {{account}}",
        "projectedAfterExpense": "На рахунку після оплати: {{amount}}",
        "projectedAfterIncome": "На рахунку після доходу: {{amount}}",
        "accountInsufficient": "Недостатньо коштів на цьому рахунку/картці",
        "riskBadge": "Потрібні кошти",
        "actionBadge": "Потрібна дія",
        "missedCommitmentBadge": "Обіцянку пропущено",
        "pendingAction": "Задача очікує: підтвердьте або скасуйте її",
        "overdueAction": "Прострочена задача: підтвердьте або скасуйте її",
        "overdueActionWithDate": "Прострочена задача, початкова дата {{date}}. Підтвердьте або скасуйте її.",
        "missedCommitmentAction": "Пропущена обіцянка: підтвердьте або скасуйте сьогодні.",
        "missedCommitmentActionWithDays": "Пропущена обіцянка: запізнення {{count}} днів. Підтвердьте або скасуйте сьогодні.",
        "boostButton": {
          "manage": "Керувати персональними бустами",
          "start": "Запустити персональний буст"
        },
        "boostTooltip": {
          "manage": "Керуйте ✨ персональними бустами",
          "count_one": "Заплановано {{count}} буст на цей день.",
          "count_few": "Заплановано {{count}} бусти на цей день.",
          "count_many": "Заплановано {{count}} бустів на цей день.",
          "count_other": "Заплановано {{count}} бустів на цей день.",
          "start": "Запустіть ✨ Personal Boost, щоб скоригувати цей день."
        }
      },
      "adjustments": {
        "boostPrefix": "✨ Personal Boost:",
        "repaymentPlanPrefix": "План погашення:",
        "defaultIncome": "Буст доходу",
        "defaultExpense": "Буст витрат",
        "boostEntry": "Запис ✨ Personal Boost",
        "noDescription": "Без опису",
        "logTitle": "✨ Журнал Personal Boost",
        "incomeTag": "Буст доходу",
        "expenseTag": "Буст витрат",
        "removeConfirm": "Видалити цей ✨ Personal Boost?",
        "removeAction": "Видалити",
        "modalTitle": "✨ Прогноз Personal Boost",
        "modalTitleForDate": "✨ Personal Boost для {{date}}",
        "modalOk": "Зберегти Personal Boost",
        "modalCancel": "Закрити",
        "fields": {
          "date": "Дата",
          "type": "Тип бусту",
          "account": "Рахунок",
          "accountExpenseExtra": "Оберіть рахунок або картку, на які вплине ця витрата.",
          "accountIncomeExtra": "Оберіть рахунок, на який надійде цей дохід.",
          "accountPlaceholder": "Оберіть рахунок",
          "amount": "Сума",
          "description": "Опис",
          "descriptionExtra": "Необовʼязкова нотатка для ідентифікації бусту",
          "descriptionPlaceholder": "наприклад, Премія"
        },
        "validation": {
          "type": "Оберіть тип бусту",
          "account": "Оберіть рахунок для цього бусту",
          "amount": "Вкажіть суму бусту"
        }
      },
      "liveAccounts": {
        "title": "Активні джерела рахунків",
        "empty": "Немає"
      },
      "monthlySummary": {
        "title": "Місячний підсумок",
        "help": "Місячні підсумки прогнозних доходів і витрат, що впливають на відстежувані рахунки. Перекази не враховуються. Чистий = дохід − витрати.",
        "rowCompact": "{{month}} — {{income}} • {{expense}} • {{net}}",
        "totalNet": "Всього (Net)",
        "row": "{{month}}: Дохід {{income}} | Витрати {{expense}} | Чистий {{net}}",
        "empty": "Немає даних"
      },
      "legend": {
        "tonePositive": "Позитивний день",
        "toneNegative": "Негативний день"
      },
      "dayDrawer": {
        "title": "Деталі дня",
        "income": "Дохід",
        "expenses": "Витрати",
        "net": "Чистий результат",
        "noIncome": "Доходів не заплановано.",
        "noExpenses": "Витрат не заплановано.",
        "manageBoosts": "Керувати ✨ Personal Boosts",
        "startBoost": "Стартувати ✨ Personal Boost"
      },
      "notifications": {
        "loadFailed": "Не вдалося завантажити підсумок прогнозу",
        "historyLoadFailed": "Не вдалося завантажити запитання",
        "deleteSuccess": "Видалено",
        "deleteFailed": "Не вдалося видалити запитання",
        "dateBeforeRange": "Немає даних до початкової дати",
        "dateAfterRange": "Немає даних за межами прогнозу",
        "dateOutOfRange": "Обрана дата поза межами прогнозу",
        "boostSaved": "✨ Personal Boost збережено",
        "boostSavedStale": "✨ Personal Boost збережено, але не вдалося оновити прогноз. Дані синхронізуються під час наступного завантаження.",
        "boostSaveFailed": "Не вдалося зберегти ✨ Personal Boost",
        "boostRemoved": "✨ Personal Boost видалено",
        "boostRemovedStale": "✨ Personal Boost видалено, але не вдалося оновити прогноз. Дані синхронізуються під час наступного завантаження.",
        "boostRemoveFailed": "Не вдалося видалити ✨ Personal Boost"
      }
    }
  }
};

export default resources;
